import React, { useState, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import { useTranslation } from "react-i18next";
import { createProgramRequestNotification, canRequestNewProgram } from "@features/programs/services/notificationService";

const RequestProgramModal = ({ 
  show, 
  onHide, 
  programId = null, 
  onSuccess 
}) => {
  const { t } = useTranslation();
  const [requestMessage, setRequestMessage] = useState("");
  const [isSubmittingRequest, setIsSubmittingRequest] = useState(false);
  const [canRequest, setCanRequest] = useState(true);
  const [daysUntilNext, setDaysUntilNext] = useState(0);
  const [isCheckingEligibility, setIsCheckingEligibility] = useState(false);

  // Check eligibility when modal opens
  useEffect(() => {
    if (show) {
      checkEligibility();
    }
  }, [show]);

  const checkEligibility = async () => {
    setIsCheckingEligibility(true);
    try {
      const response = await canRequestNewProgram();
      setCanRequest(response.can_request);
      setDaysUntilNext(response.days_until_next);
    } catch (error) {
      console.error("Error checking eligibility:", error);
      setCanRequest(true); // Fallback to allowing request
    } finally {
      setIsCheckingEligibility(false);
    }
  };

  const handleSubmitRequest = async () => {
    if (!canRequest) {
      const message = daysUntilNext === 1 
        ? t("program.request_cooldown_single")
        : t("program.request_cooldown", { days: daysUntilNext });
      alert(message);
      return;
    }

    if (!requestMessage.trim()) {
      alert(t("program.request_message_required"));
      return;
    }

    setIsSubmittingRequest(true);
    try {
      await createProgramRequestNotification(programId, requestMessage);
      alert(t("program.request_sent_successfully"));
      onSuccess?.();
      handleClose();
    } catch (error) {
      console.error("Error sending request:", error);
      alert(t("program.request_error"));
    } finally {
      setIsSubmittingRequest(false);
    }
  };

  const handleClose = () => {
    setRequestMessage("");
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>{t("program.request_new_program")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>{t("program.request_message_label")}</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              placeholder={t("program.request_message_placeholder")}
              value={requestMessage}
              onChange={(e) => setRequestMessage(e.target.value)}
            />
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          {t("actions.cancel")}
        </Button>
        {canRequest ? (
          <Button 
            variant="primary" 
            onClick={handleSubmitRequest}
            disabled={isSubmittingRequest || isCheckingEligibility}
          >
            {isSubmittingRequest ? t("actions.sending") : t("actions.send_request")}
          </Button>
        ) : (
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip id="request-tooltip">
                {daysUntilNext === 1 
                  ? t("program.request_cooldown_single")
                  : t("program.request_cooldown", { days: daysUntilNext })
                }
              </Tooltip>
            }
          >
            <span className="d-inline-block">
              <Button 
                variant="primary" 
                disabled
                style={{ pointerEvents: 'none' }}
              >
                {t("actions.send_request")}
              </Button>
            </span>
          </OverlayTrigger>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default RequestProgramModal;
